import { FilterSelect, SearchInput, SortIcon } from "@marketplace/shared/ui";
import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Text
} from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiPlus } from "react-icons/hi";

import { NoAutomationIllustration } from "@/assets/images/no-automation-ilustration";
import { Pagination } from "@/components";

import { AutomationDeleteModal } from "../../components/automation-delete-modal/automation-delete-modal";
import { AutomationDetailModal } from "../../components/automation-detail-modal/automation-detail-modal";
import { AutomationListTable } from "../../components/automation-list-table/automation-list-table";
import { AutomationSetupNotifyModal } from "../../components/automation-setup-notify-modal/automation-setup-notify-modal";
import { useGetAutomationListQuery } from "../../hooks/queries/use-get-automation-list-query";
import { useSortByOptions } from "../../hooks/use-sort-by-options";
import type { AutomationSortValue } from "../../types";

const route = getRouteApi("/products/automation/");

export const AutomationListPage = () => {
  const { q, sort, page } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("product", { keyPrefix: "automation" });
  const { t: tCommon } = useTranslation("common");
  const sortByOptions = useSortByOptions();

  const [search, setSearch] = useState(q ?? "");

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    setSearch(q ?? "");
  }, [q]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      navigate({
        search: (prev) => ({
          ...prev,
          q: search,
          page: 1
        })
      });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSortByChange = (value: AutomationSortValue) => {
    navigate({
      search: (prev) => ({
        ...prev,
        sort: value,
        page: 1
      })
    });
  };

  const handleAddProduct = () => {
    navigate({ to: "/products/automation/create" });
  };

  const { data, isLoading } = useGetAutomationListQuery({
    queryParams: {
      page: route.useSearch().page ?? 1,
      limit: 10,
      q,
      sort
    }
  });

  const productListData = data?.data ?? [];
  const metadata = data?.metadata;

  return (
    <>
      <AutomationDeleteModal />
      <AutomationSetupNotifyModal />
      <AutomationDetailModal />

      <div className="flex justify-between items-center mb-6">
        <Text type="heading" size="l" className="text-humble-blue-500">
          {t("listPage.title")}
        </Text>
        <Button
          variant="primary"
          rightSection={<HiPlus size={16} />}
          onClick={handleAddProduct}
        >
          {t("listPage.addProduct")}
        </Button>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <SearchInput
            className="lg:w-[634px]"
            placeholder={t("listPage.searchPlaceholder")}
            onChange={handleSearchChange}
            value={search}
          />
        </div>
        <div className="flex gap-x-[14px]">
          <FilterSelect
            label={tCommon("sortBy")}
            options={sortByOptions}
            value={sort ?? "newest"}
            onChange={(v) => handleSortByChange(v as AutomationSortValue)}
            width={179}
            icon={<SortIcon />}
            dropdownPosition="bottom-end"
          />
        </div>
      </div>

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering
        condition={!isLoading && productListData.length > 0}
      >
        <div className="space-y-6">
          <AutomationListTable records={productListData} page={page} />

          <Pagination
            currentPageTotal={productListData.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: value
                })
              })
            }
          />
        </div>
      </ConditionalRendering>

      <ConditionalRendering
        condition={!isLoading && productListData.length === 0}
      >
        <div className="flex flex-col justify-center items-center gap-y-6 mt-[56px]">
          <NoAutomationIllustration />
          <div>
            <Text type="heading" size="xl" className="mb-2 text-fjord-grey-500">
              {t("listPage.empty.title")}
            </Text>
            <Text type="paragraph" size="l" className="text-fjord-grey-300">
              {t("listPage.empty.description")}
            </Text>
          </div>
          <Button
            onClick={handleAddProduct}
            variant="primary"
            rightSection={<HiPlus size={16} />}
          >
            {t("listPage.addProduct")}
          </Button>
        </div>
      </ConditionalRendering>
    </>
  );
};
